/**
 * Phase 15.4 — Logistics Hub tabs:
 *   - PendingLocationsTab : approve / reject driver-submitted locations
 *   - MarketReceivablesTab: open/settled list + receive-payment action
 *   - TruckPrimaryDriverDialog: tiny modal to assign primary driver to a truck
 */
import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  MapPin, CheckCircle2, X, Phone, IndianRupee, AlertCircle,
  Wallet, User,
} from "lucide-react";
import { toast } from "sonner";

const fmt = (n) => "₹" + Math.round(Number(n || 0)).toLocaleString("en-IN");

export function PendingLocationsTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rejectFor, setRejectFor] = useState(null);
  const [mapPreviewFor, setMapPreviewFor] = useState(null);
  const [reason, setReason] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/logistics/locations/pending");
      setRows(data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const approve = async (id) => {
    try {
      await api.post(`/logistics/locations/${id}/approve`);
      toast.success("Location approved");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  const reject = async () => {
    if (!rejectFor) return;
    try {
      await api.post(`/logistics/locations/${rejectFor.id}/reject`, null, {
        params: { reason },
      });
      toast.success("Location rejected");
      setRejectFor(null); setReason("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-3" data-testid="pending-locations-tab">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <AlertCircle className="h-4 w-4 text-amber-600" />
          <span className="text-sm font-semibold">
            Driver-submitted locations awaiting approval
          </span>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="pending-loc-refresh">
          Refresh
        </Button>
      </div>

      {loading && <div className="text-sm text-slate-500">Loading…</div>}
      {!loading && rows.length === 0 && (
        <div className="rounded-lg ring-1 ring-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-500">
          No pending locations. All caught up ✓
        </div>
      )}

      <div className="space-y-2">
        {rows.map((r) => (
          <div key={r.id}
               className="bg-white ring-1 ring-slate-200 rounded-lg p-3 grid grid-cols-1 md:grid-cols-12 gap-2"
               data-testid={`pending-loc-row-${r.id}`}>
            <div className="md:col-span-5">
              <div className="flex items-start gap-2">
                <MapPin className="h-4 w-4 text-red-600 mt-0.5" />
                <div>
                  <div className="font-semibold text-slate-900">{r.name}</div>
                  {r.name_hindi && (
                    <div className="text-xs text-slate-500">{r.name_hindi}</div>
                  )}
                  {r.address && (
                    <div className="text-xs text-slate-600 mt-0.5">{r.address}</div>
                  )}
                </div>
              </div>
            </div>
            <div className="md:col-span-3 text-xs text-slate-600 space-y-0.5">
              {r.contact_name && <div><b>Contact:</b> {r.contact_name}</div>}
              {r.contact_phone && (
                <div className="flex items-center gap-1">
                  <Phone className="h-3 w-3" /> {r.contact_phone}
                </div>
              )}
              {r.latitude != null && r.longitude != null ? (
                <div className="flex flex-wrap gap-2 text-[11px] mt-1">
                  <a className="text-bravura-700 underline"
                     href={`https://www.google.com/maps?q=${r.latitude},${r.longitude}`}
                     target="_blank" rel="noreferrer">
                    Open in Maps
                  </a>
                  <button
                    onClick={() => setMapPreviewFor(r)}
                    data-testid={`preview-map-${r.id}`}
                    className="text-emerald-700 underline">
                    Preview on map
                  </button>
                </div>
              ) : (
                <div className="text-[10px] text-amber-700 mt-1">
                  ⚠ No GPS coords — verify address before approving.
                </div>
              )}
            </div>
            <div className="md:col-span-2 text-xs text-slate-500">
              <div>By: <b>{r.created_by_driver_name || "—"}</b></div>
              <div>{(r.created_at || "").slice(0, 10)}</div>
            </div>
            <div className="md:col-span-2 flex gap-2 justify-end items-start">
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700"
                      onClick={() => approve(r.id)}
                      data-testid={`approve-${r.id}`}>
                <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Approve
              </Button>
              <Button size="sm" variant="outline" className="text-red-600"
                      onClick={() => setRejectFor(r)}
                      data-testid={`reject-${r.id}`}>
                <X className="h-3.5 w-3.5 mr-1" /> Reject
              </Button>
            </div>
          </div>
        ))}
      </div>

      {/* Google Maps preview modal */}
      <Dialog open={!!mapPreviewFor} onOpenChange={(o) => !o && setMapPreviewFor(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              <MapPin className="h-4 w-4 inline mr-1 text-red-600" />
              {mapPreviewFor?.name}
            </DialogTitle>
          </DialogHeader>
          {mapPreviewFor && (
            <div className="space-y-3">
              {mapPreviewFor.address && (
                <div className="text-xs text-slate-700">{mapPreviewFor.address}</div>
              )}
              <div className="text-xs text-slate-500 font-mono">
                Lat {mapPreviewFor.latitude?.toFixed?.(6)},
                Lng {mapPreviewFor.longitude?.toFixed?.(6)}
              </div>
              <iframe
                title="Google Maps preview"
                src={`https://www.google.com/maps?q=${mapPreviewFor.latitude},${mapPreviewFor.longitude}&z=16&output=embed`}
                className="w-full h-80 rounded-lg ring-1 ring-slate-200"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="map-preview-iframe"
              />
              <div className="flex flex-wrap gap-2 justify-between items-center">
                <a className="text-xs text-bravura-700 underline"
                   href={`https://www.google.com/maps/dir/?api=1&destination=${mapPreviewFor.latitude},${mapPreviewFor.longitude}`}
                   target="_blank" rel="noreferrer">
                  Open large map / get directions
                </a>
                <div className="flex gap-2">
                  <Button variant="outline" className="text-red-600"
                          onClick={() => { setRejectFor(mapPreviewFor); setMapPreviewFor(null); }}>
                    <X className="h-3.5 w-3.5 mr-1" /> Reject
                  </Button>
                  <Button className="bg-emerald-600 hover:bg-emerald-700"
                          onClick={async () => {
                            await approve(mapPreviewFor.id);
                            setMapPreviewFor(null);
                          }}
                          data-testid="map-preview-approve">
                    <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Approve from here
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!rejectFor} onOpenChange={(o) => !o && setRejectFor(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject location</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-slate-700">
            <b>{rejectFor?.name}</b> — please provide a reason (visible in audit log).
          </div>
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)}
                    placeholder="e.g. duplicate, incorrect address, not a real location" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectFor(null)}>Cancel</Button>
            <Button className="bg-red-600 hover:bg-red-700" onClick={reject}>
              Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


export function MarketReceivablesTab() {
  const [summary, setSummary] = useState({});
  const [rows, setRows] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [transporters, setTransporters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("open");
  const [formOpen, setFormOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [payFor, setPayFor] = useState(null);
  const [pay, setPay] = useState({ amount: "", mode: "cash", reference: "", note: "" });
  const blankForm = {
    date: new Date().toISOString().slice(0, 10),
    truck_no: "",
    trip_amount: "",
    transporter_id: "",
    _newTransporter: false,
    transporter_name: "",
    transporter_address: "",
    transporter_mobile: "",
    material_pickup_address: "",
    material_drop_address: "",
    freight: "",
    dala: "",
    builty_exp: "",
    pod_amount: "",
    pod_amount_received: "",
    pod_tracking_id: "",
    notes: "",
  };
  const [form, setForm] = useState(blankForm);

  const fmtR = (n) => "₹" + Math.round(Number(n || 0)).toLocaleString("en-IN");

  const loadMasters = async () => {
    try {
      const [tr, tt] = await Promise.all([
        api.get("/logistics/trucks"),
        api.get("/logistics/transporters"),
      ]);
      setTrucks((tr.data || []).filter((t) => t.active !== false));
      setTransporters(tt.data || []);
    } catch (e) { /* ignore */ }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = filter === "all" ? {} : { status: filter };
      const [s, r] = await Promise.all([
        api.get("/logistics/market-trips/summary"),
        api.get("/logistics/market-trips", { params }),
      ]);
      setSummary(s.data || {});
      setRows(r.data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load");
    } finally { setLoading(false); }
  };

  useEffect(() => { loadMasters(); }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [filter]);

  // When transporter selected, auto-fill address + mobile
  const onTransporterSelect = (id) => {
    if (id === "__new__") {
      setForm((f) => ({
        ...f, _newTransporter: true, transporter_id: "",
        transporter_name: "", transporter_address: "", transporter_mobile: "",
      }));
      return;
    }
    const t = transporters.find((x) => x.id === id);
    setForm((f) => ({
      ...f, _newTransporter: false, transporter_id: id,
      transporter_name: t?.name || "",
      transporter_address: t?.address || "",
      transporter_mobile: t?.mobile || "",
    }));
  };

  const openCreate = () => {
    setEditRow(null);
    setForm(blankForm);
    setFormOpen(true);
  };

  const openEdit = (r) => {
    setEditRow(r);
    setForm({
      date: r.date || blankForm.date,
      truck_no: r.truck_no || "",
      trip_amount: r.trip_amount ?? "",
      transporter_id: r.transporter_id || "",
      _newTransporter: false,
      transporter_name: r.transporter_name || "",
      transporter_address: r.transporter_address || "",
      transporter_mobile: r.transporter_mobile || "",
      material_pickup_address: r.material_pickup_address || "",
      material_drop_address: r.material_drop_address || "",
      freight: r.freight ?? "",
      dala: r.dala ?? "",
      builty_exp: r.builty_exp ?? "",
      pod_amount: r.pod_amount ?? "",
      pod_amount_received: r.pod_amount_received ?? "",
      pod_tracking_id: r.pod_tracking_id || "",
      notes: r.notes || "",
    });
    setFormOpen(true);
  };

  const saveForm = async () => {
    if (!form.date || !form.truck_no) {
      toast.warning("Date and Truck are required");
      return;
    }
    if (!form.transporter_id && !form.transporter_name) {
      toast.warning("Transporter is required");
      return;
    }
    const numKeys = ["trip_amount", "freight", "dala", "builty_exp",
                      "pod_amount", "pod_amount_received"];
    const payload = { ...form };
    numKeys.forEach((k) => {
      payload[k] = payload[k] === "" || payload[k] == null
        ? 0 : Number(payload[k]);
    });
    delete payload._newTransporter;
    if (form._newTransporter || !form.transporter_id) {
      delete payload.transporter_id;
    }
    try {
      if (editRow) {
        await api.patch(`/logistics/market-trips/${editRow.id}`, payload);
        toast.success("Market trip updated");
      } else {
        await api.post("/logistics/market-trips", payload);
        toast.success("Market trip created");
      }
      setFormOpen(false);
      setForm(blankForm);
      setEditRow(null);
      load();
      loadMasters();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    }
  };

  const onDelete = async (r) => {
    if (!window.confirm(`Delete market trip dated ${r.date} (${r.transporter_name})?`)) return;
    try {
      await api.delete(`/logistics/market-trips/${r.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const submitPayment = async () => {
    const amt = parseFloat(pay.amount);
    if (!amt || amt <= 0) { toast.warning("Enter a positive amount"); return; }
    try {
      const r = await api.post(
        `/logistics/market-trips/${payFor.id}/payments`,
        { amount: amt, mode: pay.mode || "cash",
          reference: pay.reference || null, note: pay.note || null },
      );
      toast.success("Payment added");
      setPayFor(r.data);  // refresh dialog with new payments[]
      // Update row in-place so balance/status reflect immediately
      setRows((prev) => prev.map((x) => x.id === r.data.id ? r.data : x));
      setPay({ amount: "", mode: "cash", reference: "", note: "" });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const reversePayment = async (paymentId) => {
    if (!window.confirm("Reverse this payment entry?")) return;
    try {
      const r = await api.delete(
        `/logistics/market-trips/${payFor.id}/payments/${paymentId}`);
      setPayFor(r.data);
      setRows((prev) => prev.map((x) => x.id === r.data.id ? r.data : x));
      toast.success("Payment reversed");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const uploadFile = async (row, kind, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("kind", kind);
    fd.append("file", file);
    try {
      await api.post(`/logistics/market-trips/${row.id}/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${kind === "pod_slip" ? "POD slip" : "Builty"} uploaded`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-3" data-testid="market-trip-tab">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Tile label="Open" value={summary.open_count ?? 0} icon={AlertCircle} tone="amber" />
        <Tile label="Settled" value={summary.settled_count ?? 0} icon={CheckCircle2} tone="emerald" />
        <Tile label="Trip Amount" value={fmtR(summary.total_trip_amount)}
              icon={IndianRupee} tone="blue" />
        <Tile label="Outstanding" value={fmtR(summary.total_outstanding)}
              icon={Wallet} tone="rose" />
        <Tile label="POD Pending" value={fmtR(summary.pod_outstanding)}
              icon={AlertCircle} tone="amber" />
      </div>

      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex gap-2">
          {["open", "settled", "all"].map((f) => (
            <Button key={f} size="sm"
                    variant={filter === f ? "default" : "outline"}
                    onClick={() => setFilter(f)}
                    data-testid={`mt-filter-${f}`}>
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </Button>
          ))}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load}>Refresh</Button>
          <Button size="sm" className="brand-bg brand-bg-hover text-white"
                  onClick={openCreate} data-testid="mt-add-btn">
            + Add Market Trip
          </Button>
        </div>
      </div>

      {loading && <div className="text-sm text-slate-500">Loading…</div>}
      {!loading && rows.length === 0 && (
        <div className="rounded-lg ring-1 ring-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-500">
          No market trips. Click "+ Add Market Trip" to create one.
        </div>
      )}

      {rows.length > 0 && (
        <div className="bg-white ring-1 ring-slate-200 rounded-lg overflow-x-auto overflow-y-visible">
          <table className="w-full min-w-[1400px] text-xs" data-testid="mt-table">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 sticky top-0 z-10 shadow-sm">
              <tr>
                <th className="text-left px-2 py-2">Date</th>
                <th className="text-left px-2 py-2">Truck</th>
                <th className="text-right px-2 py-2">Trip ₹</th>
                <th className="text-left px-2 py-2">Transporter</th>
                <th className="text-left px-2 py-2">Pickup → Drop</th>
                <th className="text-right px-2 py-2">Freight</th>
                <th className="text-right px-2 py-2">Dala</th>
                <th className="text-right px-2 py-2">Builty</th>
                <th className="text-right px-2 py-2">POD ₹</th>
                <th className="text-right px-2 py-2">Received</th>
                <th className="text-right px-2 py-2">Balance</th>
                <th className="text-center px-2 py-2">POD / Builty</th>
                <th className="text-center px-2 py-2">Status</th>
                <th className="text-center px-2 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-slate-100"
                    data-testid={`mt-row-${r.id}`}>
                  <td className="px-2 py-2 whitespace-nowrap">{r.date}</td>
                  <td className="px-2 py-2 font-mono">{r.truck_no}</td>
                  <td className="px-2 py-2 text-right font-mono">{fmtR(r.trip_amount)}</td>
                  <td className="px-2 py-2">
                    <div className="font-semibold">{r.transporter_name}</div>
                    {r.transporter_mobile && (
                      <a href={`tel:${r.transporter_mobile}`} className="text-[10px] text-bravura-700">
                        <Phone className="inline h-2.5 w-2.5 mr-0.5" />
                        {r.transporter_mobile}
                      </a>
                    )}
                    {r.transporter_address && (
                      <div className="text-[10px] text-slate-500 max-w-[180px] truncate"
                           title={r.transporter_address}>
                        {r.transporter_address}
                      </div>
                    )}
                  </td>
                  <td className="px-2 py-2">
                    <div className="text-[10px] text-slate-600 max-w-[200px]"
                         title={r.material_pickup_address}>
                      <span className="text-slate-400">P:</span> {r.material_pickup_address || "—"}
                    </div>
                    <div className="text-[10px] text-slate-600 max-w-[200px]"
                         title={r.material_drop_address}>
                      <span className="text-slate-400">D:</span> {r.material_drop_address || "—"}
                    </div>
                  </td>
                  <td className="px-2 py-2 text-right font-mono">{fmtR(r.freight)}</td>
                  <td className="px-2 py-2 text-right font-mono">{fmtR(r.dala)}</td>
                  <td className="px-2 py-2 text-right font-mono">{fmtR(r.builty_exp)}</td>
                  <td className="px-2 py-2 text-right font-mono">
                    {fmtR(r.pod_amount)}
                    {r.pod_amount_received > 0 && (
                      <div className="text-[10px] text-emerald-700">
                        recv {fmtR(r.pod_amount_received)}
                      </div>
                    )}
                  </td>
                  <td className="px-2 py-2 text-right font-mono text-emerald-700">
                    {fmtR(r.amount_received)}
                  </td>
                  <td className={`px-2 py-2 text-right font-mono
                                  ${r.balance > 0 ? "text-red-700" : "text-slate-500"}`}>
                    {fmtR(r.balance)}
                  </td>
                  <td className="px-2 py-2">
                    <div className="flex flex-col gap-1 items-center">
                      <UploadPill row={r} kind="pod_slip"
                                  current={r.pod_courier_slip_url}
                                  trackingId={r.pod_tracking_id}
                                  onUpload={uploadFile} />
                      <UploadPill row={r} kind="builty_copy"
                                  current={r.builty_copy_url}
                                  onUpload={uploadFile} />
                    </div>
                  </td>
                  <td className="px-2 py-2 text-center">
                    <span className={`px-2 py-0.5 text-[10px] uppercase tracking-wide rounded-full font-semibold ${
                      r.status === "open" ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"
                    }`}>{r.status}</span>
                  </td>
                  <td className="px-2 py-2">
                    <div className="flex gap-1 justify-center">
                      <Button size="sm" variant="outline"
                              onClick={() => { setPayFor(r); setPay({ amount: "", mode: "cash", reference: "", note: "" }); }}
                              data-testid={`mt-pay-${r.id}`}>
                        + Pay
                      </Button>
                      <Button size="sm" variant="ghost"
                              onClick={() => openEdit(r)}
                              data-testid={`mt-edit-${r.id}`}>
                        Edit
                      </Button>
                      <Button size="sm" variant="ghost"
                              onClick={() => onDelete(r)}
                              className="text-red-600 hover:text-red-700"
                              data-testid={`mt-delete-${r.id}`}>
                        ×
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create / Edit dialog */}
      <Dialog open={formOpen} onOpenChange={(o) => !o && setFormOpen(false)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{editRow ? "Edit market trip" : "Add market trip"}</DialogTitle>
            <DialogDescription className="text-xs">
              Capture trip + transporter + freight + POD details. Multi-part payments can be recorded later via "+ Pay".
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 max-h-[60vh] overflow-y-auto pr-2">
            <div>
              <Label className="text-xs">Date *</Label>
              <Input type="date" value={form.date}
                     onChange={(e) => setForm({ ...form, date: e.target.value })}
                     data-testid="mt-form-date" />
            </div>
            <div>
              <Label className="text-xs">Truck No *</Label>
              <Select value={form.truck_no || ""}
                      onValueChange={(v) => setForm({ ...form, truck_no: v })}>
                <SelectTrigger data-testid="mt-form-truck"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  {trucks.map((t) => (
                    <SelectItem key={t.id} value={t.truck_no}>{t.truck_no}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Trip Amount *</Label>
              <Input type="number" value={form.trip_amount}
                     onChange={(e) => setForm({ ...form, trip_amount: e.target.value })}
                     data-testid="mt-form-trip-amount" />
            </div>

            <div className="md:col-span-3 border-t pt-2">
              <Label className="text-xs font-bold">Transporter *</Label>
              {!form._newTransporter ? (
                <Select value={form.transporter_id || ""}
                        onValueChange={onTransporterSelect}>
                  <SelectTrigger data-testid="mt-form-transporter">
                    <SelectValue placeholder="Select transporter" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__new__">+ Add new transporter…</SelectItem>
                    {transporters.map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.name} {t.mobile ? `· ${t.mobile}` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <div className="grid grid-cols-3 gap-2">
                  <Input placeholder="Transporter name"
                         value={form.transporter_name}
                         onChange={(e) => setForm({ ...form, transporter_name: e.target.value })}
                         data-testid="mt-form-new-transporter-name" />
                  <Input placeholder="Mobile"
                         value={form.transporter_mobile}
                         onChange={(e) => setForm({ ...form, transporter_mobile: e.target.value })}
                         data-testid="mt-form-new-transporter-mobile" />
                  <Button variant="ghost" size="sm"
                          onClick={() => setForm({ ...form, _newTransporter: false, transporter_name: "", transporter_address: "", transporter_mobile: "" })}>
                    Cancel new
                  </Button>
                </div>
              )}
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs">Transporter Address</Label>
              <Input value={form.transporter_address}
                     onChange={(e) => setForm({ ...form, transporter_address: e.target.value })}
                     data-testid="mt-form-transporter-address" />
            </div>
            <div>
              <Label className="text-xs">Transporter Mobile</Label>
              <Input value={form.transporter_mobile}
                     onChange={(e) => setForm({ ...form, transporter_mobile: e.target.value })}
                     data-testid="mt-form-transporter-mobile" />
            </div>

            <div className="md:col-span-3 border-t pt-2">
              <Label className="text-xs font-bold">Material</Label>
            </div>
            <div className="md:col-span-3 grid grid-cols-1 md:grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Pickup Address</Label>
                <Textarea rows={2} value={form.material_pickup_address}
                          onChange={(e) => setForm({ ...form, material_pickup_address: e.target.value })}
                          data-testid="mt-form-pickup" />
              </div>
              <div>
                <Label className="text-xs">Drop Address</Label>
                <Textarea rows={2} value={form.material_drop_address}
                          onChange={(e) => setForm({ ...form, material_drop_address: e.target.value })}
                          data-testid="mt-form-drop" />
              </div>
            </div>

            <div className="md:col-span-3 border-t pt-2">
              <Label className="text-xs font-bold">Costs & POD</Label>
            </div>
            <div>
              <Label className="text-xs">Freight</Label>
              <Input type="number" value={form.freight}
                     onChange={(e) => setForm({ ...form, freight: e.target.value })}
                     data-testid="mt-form-freight" />
            </div>
            <div>
              <Label className="text-xs">Dala</Label>
              <Input type="number" value={form.dala}
                     onChange={(e) => setForm({ ...form, dala: e.target.value })}
                     data-testid="mt-form-dala" />
            </div>
            <div>
              <Label className="text-xs">Builty Expense</Label>
              <Input type="number" value={form.builty_exp}
                     onChange={(e) => setForm({ ...form, builty_exp: e.target.value })}
                     data-testid="mt-form-builty" />
            </div>
            <div>
              <Label className="text-xs">POD Amount</Label>
              <Input type="number" value={form.pod_amount}
                     onChange={(e) => setForm({ ...form, pod_amount: e.target.value })}
                     data-testid="mt-form-pod" />
            </div>
            <div>
              <Label className="text-xs">POD Amount Received</Label>
              <Input type="number" value={form.pod_amount_received}
                     onChange={(e) => setForm({ ...form, pod_amount_received: e.target.value })}
                     data-testid="mt-form-pod-recv" />
            </div>
            <div>
              <Label className="text-xs">POD Tracking ID</Label>
              <Input value={form.pod_tracking_id}
                     onChange={(e) => setForm({ ...form, pod_tracking_id: e.target.value })}
                     placeholder="e.g. BLR1234567"
                     data-testid="mt-form-pod-tracking" />
            </div>

            <div className="md:col-span-3">
              <Label className="text-xs">Notes</Label>
              <Textarea rows={2} value={form.notes}
                        onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFormOpen(false)}>Cancel</Button>
            <Button onClick={saveForm} className="brand-bg brand-bg-hover text-white"
                    data-testid="mt-form-save">
              {editRow ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment dialog (multi-payment) */}
      <Dialog open={!!payFor} onOpenChange={(o) => !o && setPayFor(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              Payments — {payFor?.transporter_name} · Trip {fmtR(payFor?.trip_amount)}
            </DialogTitle>
            <DialogDescription className="text-xs">
              Add multiple part-payments. Balance auto-updates and trip is marked settled when balance reaches zero.
            </DialogDescription>
          </DialogHeader>
          {payFor && (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-2 text-xs">
                <div className="bg-slate-50 ring-1 ring-slate-200 rounded p-2">
                  <div className="text-[10px] uppercase text-slate-500">Trip Amount</div>
                  <div className="font-bold">{fmtR(payFor.trip_amount)}</div>
                </div>
                <div className="bg-emerald-50 ring-1 ring-emerald-200 rounded p-2">
                  <div className="text-[10px] uppercase text-slate-500">Received</div>
                  <div className="font-bold text-emerald-700">{fmtR(payFor.amount_received)}</div>
                </div>
                <div className="bg-red-50 ring-1 ring-red-200 rounded p-2">
                  <div className="text-[10px] uppercase text-slate-500">Balance</div>
                  <div className="font-bold text-red-700">{fmtR(payFor.balance)}</div>
                </div>
              </div>

              {/* Payment history */}
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">
                  History ({(payFor.payments || []).length})
                </div>
                {(payFor.payments || []).length === 0 ? (
                  <div className="text-xs text-slate-400">No payments yet.</div>
                ) : (
                  <div className="border rounded max-h-48 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="text-left px-2 py-1">Date</th>
                          <th className="text-right px-2 py-1">Amount</th>
                          <th className="text-left px-2 py-1">Mode</th>
                          <th className="text-left px-2 py-1">Ref</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {(payFor.payments || []).map((p) => (
                          <tr key={p.id} className="border-t">
                            <td className="px-2 py-1">{p.date}</td>
                            <td className="px-2 py-1 text-right font-mono">{fmtR(p.amount)}</td>
                            <td className="px-2 py-1 uppercase text-[10px]">{p.mode}</td>
                            <td className="px-2 py-1 text-[10px]">{p.reference || "—"}</td>
                            <td className="px-2 py-1 text-right">
                              <button onClick={() => reversePayment(p.id)}
                                      className="text-[10px] text-red-600 hover:text-red-800"
                                      data-testid={`mt-pay-reverse-${p.id}`}>
                                Reverse
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Add new payment */}
              {payFor.status === "open" && (
                <div className="border-t pt-3">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Add payment</div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    <div>
                      <Label className="text-xs">Amount (₹)</Label>
                      <Input type="number" value={pay.amount}
                             onChange={(e) => setPay({ ...pay, amount: e.target.value })}
                             data-testid="mt-pay-amount" />
                    </div>
                    <div>
                      <Label className="text-xs">Mode</Label>
                      <Select value={pay.mode} onValueChange={(v) => setPay({ ...pay, mode: v })}>
                        <SelectTrigger data-testid="mt-pay-mode"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="cash">Cash</SelectItem>
                          <SelectItem value="bank">Bank</SelectItem>
                          <SelectItem value="upi">UPI</SelectItem>
                          <SelectItem value="cheque">Cheque</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Reference</Label>
                      <Input value={pay.reference}
                             onChange={(e) => setPay({ ...pay, reference: e.target.value })}
                             placeholder="UTR / Cheque #"
                             data-testid="mt-pay-reference" />
                    </div>
                    <div>
                      <Label className="text-xs">Note</Label>
                      <Input value={pay.note}
                             onChange={(e) => setPay({ ...pay, note: e.target.value })} />
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayFor(null)}>Close</Button>
            {payFor?.status === "open" && (
              <Button onClick={submitPayment} className="brand-bg brand-bg-hover text-white"
                      data-testid="mt-pay-submit">
                + Add Payment
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function UploadPill({ row, kind, current, trackingId, onUpload }) {
  const inputId = `mt-${kind}-${row.id}`;
  const label = kind === "pod_slip" ? "POD" : "Builty";
  return (
    <div className="flex items-center gap-1">
      {current ? (
        <a href={current} target="_blank" rel="noreferrer"
           className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200">
          {label} ✓
        </a>
      ) : trackingId ? (
        <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-50 text-slate-600 ring-1 ring-slate-200">
          {label}: {trackingId}
        </span>
      ) : (
        <span className="text-[10px] text-slate-400">{label}: —</span>
      )}
      <label htmlFor={inputId}
             className="text-[10px] underline text-bravura-700 cursor-pointer"
             data-testid={`mt-upload-${kind}-${row.id}`}>
        {current ? "Re-upload" : "Upload"}
        <input id={inputId} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp"
               className="hidden"
               onChange={(e) => onUpload(row, kind, e.target.files?.[0])} />
      </label>
    </div>
  );
}

function Tile({ label, value, icon: Icon, tone }) {
  const cls = {
    amber: "text-amber-700", emerald: "text-emerald-700",
    rose: "text-red-700", blue: "text-blue-700",
  }[tone];
  return (
    <div className="bg-white ring-1 ring-slate-200 rounded-lg p-3">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-slate-500">
        <Icon className="h-3 w-3" /> {label}
      </div>
      <div className={`text-lg font-bold mt-0.5 ${cls}`}>{value}</div>
    </div>
  );
}


/**
 * Tiny dialog used from TrucksTab to assign / clear the 1:1 primary driver.
 * Drivers list fetched once on mount.
 */
export function TruckPrimaryDriverDialog({ truck, open, onClose, onSaved }) {
  const [drivers, setDrivers] = useState([]);
  const [driverId, setDriverId] = useState(truck?.primary_driver_id || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDriverId(truck?.primary_driver_id || "");
    api.get("/logistics/drivers").then(r => setDrivers(r.data || []))
       .catch(() => api.get("/employees", { params: { category: "driver", limit: 500 } })
                       .then(r => setDrivers(r.data?.items || r.data || []))
                       .catch(() => {}));
  }, [open, truck]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/logistics/trucks/${truck.id}/primary-driver`, {
        primary_driver_id: driverId || null,
      });
      toast.success("Primary driver updated");
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Primary driver for {truck?.truck_no}</DialogTitle>
        </DialogHeader>
        <div className="space-y-2">
          <Label className="text-xs">Driver</Label>
          <Select value={driverId || "__none__"}
                  onValueChange={(v) => setDriverId(v === "__none__" ? "" : v)}>
            <SelectTrigger data-testid="primary-driver-select">
              <SelectValue placeholder="Select driver…" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">— None / Unassign —</SelectItem>
              {drivers.map((d) => (
                <SelectItem key={d.id} value={d.id}>
                  {d.name} {d.employee_code ? `(${d.employee_code})` : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-[11px] text-slate-500">
            Driver login mein yeh truck auto-select hogi. 1:1 enforced — agar
            yeh driver kisi aur truck pe primary hai, woh link clear ho jaayega.
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="primary-driver-save">
            <User className="h-3.5 w-3.5 mr-1" /> Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Label({ children, className }) {
  return (
    <label className={`text-xs font-medium text-slate-700 ${className || ""}`}>
      {children}
    </label>
  );
}
